import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AdmittedPatientsModal from "../../components/ipd/admission/AdmittedPatientsModal";
import CommonButton from "../../components/common11/Buttons/CommonButton";
import PrintButton from "../../components/common11/Buttons/PrintButton";
import CommonCheckbox from "../../components/common11/formInputs/CommonCheckbox";
import CommonDatePicker from "../../components/common11/formInputs/CommonDatePicker";
import CommonTimePicker from "../../components/common11/formInputs/CommonTimePicker";
import InputAreaField from "../../components/common11/formInputs/InputAreaField";
import InputField from "../../components/common11/formInputs/InputField";
import SearchDropdown from "../../components/common11/formInputs/SearchDropdown";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  showWarningToast,
} from "../../components/common11/toastUtils";
import {
  getOpIpPatients,
  mapOpIpPatientToAdmissionRow,
} from "../../services/opIpPatientService";
import { mapSummaryToFormValues } from "../../services/dischargeSummaryService";
import {
  fetchDischargeSummaryByAdmission,
  fetchDischargeTemplates,
  fetchFollowUpInstructions,
  resetDischargeSummary,
  saveDischargeSummaryToApi,
  searchDischargeDoses,
  searchDischargeDrugs,
  setDischargeSummaryId,
  setPatient,
} from "../../store/slices/dischargeSummarySlice";
import { rtfToPlainText } from "../../utils/rtfToPlainText";

const DISCHARGE_TABS = [
  { id: "finalDiagnosis", label: "Final Diagnosis" },
  { id: "clinicalFinding", label: "Clinical Finding" },
  { id: "history", label: "History" },
  { id: "investigation", label: "Investigation" },
  { id: "operativeNote", label: "Operative Note" },
  { id: "courseInHospital", label: "Course in the Hospital" },
  { id: "treatmentDetails", label: "Treatment Details" },
  { id: "followUp", label: "Follow Up Instruction" },
];

const MOCK_DOCTOR_OPTIONS = [
  { id: 1, value: 1, label: "Dr. Sanjeev Kulkarni" },
  { id: 2, value: 2, label: "Dr Mahesh Mandora" },
  { id: 3, value: 3, label: "Dr Anita Rao" },
  { id: 4, value: 4, label: "Dr Prasad Deshmukh" },
];

const DISCHARGE_TYPE_OPTIONS = [
  { id: "normal", value: "normal", label: "Normal" },
  { id: "lama", value: "lama", label: "LAMA" },
  { id: "dama", value: "dama", label: "DAMA" },
  { id: "death", value: "death", label: "Death" },
];

const CONTACT_WHEN_OPTIONS = [
  { id: "immediately", value: "immediately", label: "Immediately" },
  { id: "24hrs", value: "24hrs", label: "Within 24 Hours" },
  { id: "48hrs", value: "48hrs", label: "Within 48 Hours" },
];

const CONTACT_HOW_OPTIONS = [
  { id: "phone", value: "phone", label: "Phone" },
  { id: "visit", value: "visit", label: "Visit" },
  { id: "emergency", value: "emergency", label: "Emergency" },
];

const CONDITION_ON_DISCHARGE_OPTIONS = [
  {
    id: "cond-1",
    value: "cond-1",
    label: "Stable",
    description: "Patient is stable on discharge. Vital signs within normal limits.",
  },
  {
    id: "cond-2",
    value: "cond-2",
    label: "Improved",
    description: "Patient condition improved compared to admission. Fit for discharge.",
  },
  {
    id: "cond-3",
    value: "cond-3",
    label: "Critical",
    description: "Discharged on request / against advice. Patient informed of risks.",
  },
];

const ANAESTHESIA_TYPE_OPTIONS = [
  { id: "general", value: "general", label: "General" },
  { id: "spinal", value: "spinal", label: "Spinal" },
  { id: "local", value: "local", label: "Local" },
  { id: "epidural", value: "epidural", label: "Epidural" },
];

const CLINICAL_FINDING_DEFAULT =
  "CHIEF COMPLAINTS :\n\nPRESENT ILLNESS :\n\nGENERAL EXAMINATION :\n\nSYSTEMATIC EXAMINATION :\n\nLOCAL EXAMINATION :\n\nSPECIAL EXAMINATION :";

const HISTORY_DEFAULT =
  "PAST HISTORY :\n\nPERSONAL HISTORY :\n\nFAMILY HISTORY :\n\nPREVIOUS TREATMENT :\n\nPSYCOSOCIAL HISTORY :\n\nOBSTETRICS HISTORY :";

const INVESTIGATION_DEFAULT =
  "Pathology Investigations :\nParamenter Name\t\tResult Value";

const TREATMENT_TABLE_COLUMNS = [
  { key: "drug", label: "Drug" },
  { key: "dose", label: "Dose" },
  { key: "duration", label: "Duration" },
  { key: "durationIn", label: "Dura. In" },
  { key: "qty", label: "Qty" },
  { key: "instruction", label: "Instruction" },
];

const INVESTIGATION_TABLE_COLUMNS = [
  { key: "srNo", label: "Sr No" },
  { key: "testDate", label: "Test Date" },
  { key: "testName", label: "Test Name" },
  { key: "parameter", label: "Parameter" },
  { key: "result", label: "Result" },
  { key: "normalRange", label: "Normal Range" },
];

const createEmptyTreatmentRow = () => ({
  id: `row-${Date.now()}-${Math.random()}`,
  drug: "",
  dose: "",
  duration: "",
  durationIn: "",
  qty: "",
  instruction: "",
});

const createEmptyInvestigationRow = (index) => ({
  id: `inv-${Date.now()}-${Math.random()}`,
  srNo: String(index + 1),
  testDate: "",
  testName: "",
  parameter: "",
  result: "",
  normalRange: "",
});

const DISCHARGE_SUMMARY_TEMPLATES = [
  {
    id: "ds-general",
    value: "ds-general",
    label: "General Discharge Summary",
    data: {
      finalDiagnosis: "Primary Diagnosis :\nSecondary Diagnosis :\nComorbidities :",
      clinicalFinding: CLINICAL_FINDING_DEFAULT,
      bp: "",
      pulse: "",
      resp: "",
      spo2: "",
      history: HISTORY_DEFAULT,
      allergy: "",
      reaction: "",
      investigation: INVESTIGATION_DEFAULT,
      otherInvestigation: "",
      operativeNotes:
        "Pre-operative diagnosis :\n\nOperative findings :\n\nProcedure performed :\n\nPost-operative plan :",
      implantDetails: "",
      instrumentDetails: "",
      procedureIndication: "",
      operativeProc: "",
      treatmentGivenText: "Treatment Given :\n",
      treatmentAdvisedText: "Treatment Advised After Discharge :\n",
      followUpInstructions:
        "Follow up after 7 days.\nReport immediately if fever, pain, or bleeding.",
      conditionOnDischarge: "Stable on discharge.",
      doctorNotes: "",
      dietAdvice: "Normal diet as tolerated.",
      physiotherapy: "",
      treatmentGivenRows: [createEmptyTreatmentRow()],
      treatmentAdvisedRows: [createEmptyTreatmentRow()],
      investigationRows: [createEmptyInvestigationRow(0)],
    },
  },
  {
    id: "ds-surgical",
    value: "ds-surgical",
    label: "Surgical Discharge Summary",
    data: {
      finalDiagnosis: "Post-operative recovery — surgical case.",
      clinicalFinding: CLINICAL_FINDING_DEFAULT,
      bp: "",
      pulse: "",
      resp: "",
      spo2: "",
      history: HISTORY_DEFAULT,
      allergy: "",
      reaction: "",
      investigation: INVESTIGATION_DEFAULT,
      otherInvestigation: "",
      operativeNotes:
        "Patient prepared, painted and draped under aseptic precautions.\nProcedure completed uneventfully.\nPost-operative plan documented.",
      implantDetails: "",
      instrumentDetails: "",
      procedureIndication: "",
      operativeProc: "",
      treatmentGivenText: "IV antibiotics and analgesics during hospital stay.",
      treatmentAdvisedText: "Oral antibiotics and wound care for 7 days.",
      followUpInstructions: "Suture removal after 10 days.",
      conditionOnDischarge: "Improved.",
      doctorNotes: "",
      dietAdvice: "Soft diet for 3 days.",
      physiotherapy: "Mobilisation as advised.",
      treatmentGivenRows: [createEmptyTreatmentRow()],
      treatmentAdvisedRows: [createEmptyTreatmentRow()],
      investigationRows: [createEmptyInvestigationRow(0)],
    },
  },
];

const mapAdmissionRowToPatient = (row) => ({
  registrationNumber: String(row.admissionId || row.uhid || ""),
  uhid: row.uhid || "",
  patientName: row.patientName || "",
  contact: row.contact || "",
  patientGender: row.gender || "",
  patientAge: row.age || "",
  doctorName: row.doctorName || "",
  doctorId: row.doctorId || null,
  wardName: row.wardName || "",
  bedNo: row.bedNo || "",
  admissionId: row.admissionId || 0,
  regId: row.regId || null,
  dateOfAdmission: row.admDateTime || "",
  dateOfDischarge: "",
  isMLC: "NO",
  // Populated when Get/Insert discharge SPs are wired
  dischargeId: row.dischargeId || null,
  dischargeSummaryId: row.dischargeSummaryId || null,
});

const getDefaultFormValues = () => {
  const now = new Date();
  const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}`;
  return {
    patientSearch: null,
    template: null,
    finalDiagnosis: "",
    clinicalFinding: CLINICAL_FINDING_DEFAULT,
    bp: "",
    pulse: "",
    resp: "",
    spo2: "",
    history: HISTORY_DEFAULT,
    allergy: "",
    reaction: "",
    investigation: INVESTIGATION_DEFAULT,
    otherInvestigation: "",
    operativeNotes: "",
    operativeDate: new Date().toISOString().split("T")[0],
    operativeTime: timeStr,
    fetchLatestOperativeNotes: false,
    implantDetails: "",
    instrumentDetails: "",
    procedureIndication: "",
    operativeProc: "",
    surgeonName: null,
    asstSurgeonName: null,
    anaestheticsName: null,
    anaesthesiaType: null,
    treatmentGivenText: "",
    treatmentAdvisedText: "",
    useReadymadePreGiven: false,
    useReadymadePreAdvised: false,
    followUpDate: new Date().toISOString().split("T")[0],
    followUpTime: timeStr,
    bookAppointment: false,
    followUpInstructionSelect: null,
    followUpInstructions: "",
    conditionOnDischargeSelect: null,
    conditionOnDischarge: "",
    doctorNotes: "",
    doctor1: null,
    doctor2: null,
    doctor3: null,
    dischargeType: null,
    doctorAssistant: "",
    weight: "0",
    emergencyContact: "",
    whenToContact: null,
    howToContact: null,
    dietAdvice: "",
    physiotherapy: "",
  };
};

const toTextOption = (text) => {
  const value = String(text || "").trim();
  if (!value) return null;
  return { id: value, value, label: value, description: value };
};

function EditableDataTable({
  columns,
  rows,
  onCellChange,
  highlightFirstCell = false,
  minHeight = "",
  control,
  namePrefix = "",
  drugOptions = [],
  doseOptions = [],
  onDrugSearch,
  onDoseSearch,
  drugsLoading = false,
  dosesLoading = false,
}) {
  return (
    <div className={`rounded border border-gray-300 overflow-auto ${minHeight}`}>
      <table className="min-w-full text-[11px]">
        <thead className="bg-teal-700 text-white sticky top-0">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-2 py-4 text-center text-gray-500 border-t border-gray-200"
              >
                No records.
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => (
              <tr key={row.id} className="border-t border-gray-200">
                {columns.map((col, colIndex) => {
                  const cellClass = `px-1 py-0.5 ${
                    highlightFirstCell && rowIndex === 0 && colIndex === 0
                      ? "bg-yellow-100"
                      : ""
                  }`;

                  if (col.key === "drug" && control) {
                    const selected = toTextOption(row.drug);
                    const options =
                      selected &&
                      !drugOptions.some((o) => String(o.label) === selected.label)
                        ? [selected, ...drugOptions]
                        : drugOptions;
                    return (
                      <td key={col.key} className={`${cellClass} min-w-[10rem]`}>
                        <SearchDropdown
                          name={`${namePrefix}_drug_${row.id}`}
                          control={control}
                          dataArray={options}
                          value={selected}
                          onChange={(opt) =>
                            onCellChange(
                              row.id,
                              "drug",
                              opt?.description || opt?.label || ""
                            )
                          }
                          onSearch={onDrugSearch}
                          isLoading={drugsLoading}
                          isSearchable
                          isClearable
                          isFloatingLabel={false}
                          fontSize="text-xs"
                          selectSize="h-8"
                          menuPortal
                          placeholder="Drug"
                        />
                      </td>
                    );
                  }

                  if (col.key === "dose" && control) {
                    const selected = toTextOption(row.dose);
                    const options =
                      selected &&
                      !doseOptions.some((o) => String(o.label) === selected.label)
                        ? [selected, ...doseOptions]
                        : doseOptions;
                    return (
                      <td key={col.key} className={`${cellClass} min-w-[8rem]`}>
                        <SearchDropdown
                          name={`${namePrefix}_dose_${row.id}`}
                          control={control}
                          dataArray={options}
                          value={selected}
                          onChange={(opt) =>
                            onCellChange(
                              row.id,
                              "dose",
                              opt?.description || opt?.label || ""
                            )
                          }
                          onSearch={onDoseSearch}
                          isLoading={dosesLoading}
                          isSearchable
                          isClearable
                          isFloatingLabel={false}
                          fontSize="text-xs"
                          selectSize="h-8"
                          menuPortal
                          placeholder="Dose"
                        />
                      </td>
                    );
                  }

                  return (
                    <td key={col.key} className={cellClass}>
                      <input
                        type="text"
                        value={row[col.key] || ""}
                        onChange={(e) => onCellChange(row.id, col.key, e.target.value)}
                        className="w-full min-w-[4rem] border border-gray-200 rounded px-1 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#217E90]"
                      />
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function TreatmentTableSection({
  title,
  checkboxName,
  control,
  rows,
  onCellChange,
  namePrefix,
  drugOptions,
  doseOptions,
  onDrugSearch,
  onDoseSearch,
  drugsLoading,
  dosesLoading,
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 flex-nowrap min-w-0">
        <p className="text-xs font-semibold text-[#217E90] whitespace-nowrap shrink-0">
          {title}
        </p>
        <div className="shrink-0">
          <CommonCheckbox
            name={checkboxName}
            control={control}
            label="Use Readymade Pre."
            fontSize="text-xs"
            checkboxSize="h-3.5 w-3.5"
          />
        </div>
      </div>
      <EditableDataTable
        columns={TREATMENT_TABLE_COLUMNS}
        rows={rows}
        onCellChange={onCellChange}
        highlightFirstCell
        minHeight="min-h-[10rem]"
        control={control}
        namePrefix={namePrefix}
        drugOptions={drugOptions}
        doseOptions={doseOptions}
        onDrugSearch={onDrugSearch}
        onDoseSearch={onDoseSearch}
        drugsLoading={drugsLoading}
        dosesLoading={dosesLoading}
      />
    </div>
  );
}

const DischargeSummary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const {
    patient,
    saving,
    dischargeSummaryId,
    templates,
    followUpInstructions,
    followUpInstructionsLoading,
    drugs,
    doses,
    drugsLoading,
    dosesLoading,
  } = useSelector((state) => state.dischargeSummary);

  const [activeTab, setActiveTab] = useState("finalDiagnosis");
  const [admittedModalOpen, setAdmittedModalOpen] = useState(false);
  const [admissionRows, setAdmissionRows] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [treatmentGivenRows, setTreatmentGivenRows] = useState([createEmptyTreatmentRow()]);
  const [treatmentAdvisedRows, setTreatmentAdvisedRows] = useState([createEmptyTreatmentRow()]);
  const [investigationRows, setInvestigationRows] = useState([createEmptyInvestigationRow(0)]);

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: getDefaultFormValues(),
  });

  const loadIpPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const [admitted, discharged] = await Promise.all([
        getOpIpPatients({ opIpType: 1, isDischarged: false }),
        getOpIpPatients({ opIpType: 1, isDischarged: true }),
      ]);
      const mapped = [...(admitted || []), ...(discharged || [])].map(
        mapOpIpPatientToAdmissionRow
      );
      const uniqueByAdmission = [];
      const seen = new Set();
      mapped.forEach((row) => {
        const key = row.admissionId || row.uhid;
        if (key == null || seen.has(key)) return;
        seen.add(key);
        uniqueByAdmission.push(row);
      });
      setAdmissionRows(uniqueByAdmission);
    } catch (error) {
      setAdmissionRows([]);
      showErrorToast(error.message || "Failed to load patients");
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIpPatients();
  }, [loadIpPatients]);

  useEffect(() => {
    dispatch(fetchDischargeTemplates());
    dispatch(fetchFollowUpInstructions());
    dispatch(searchDischargeDrugs(""));
    dispatch(searchDischargeDoses(""));
  }, [dispatch]);

  const handleDrugSearch = useCallback(
    (term) => dispatch(searchDischargeDrugs(term || "")),
    [dispatch]
  );

  const handleDoseSearch = useCallback(
    (term) => dispatch(searchDischargeDoses(term || "")),
    [dispatch]
  );

  const patientSearchOptions = useMemo(
    () =>
      admissionRows
        .filter((row) =>
          ["Admitted", "Discharge initiated", "Discharged"].includes(row.status)
        )
        .map((row) => ({
          id: row.admissionId,
          label: [
            row.patientName,
            row.uhid,
            row.contact,
            row.age ? `${row.age}Y` : null,
            row.gender,
          ]
            .filter(Boolean)
            .join(" | "),
          value: row.patientName,
          row,
        })),
    [admissionRows]
  );

  const templateOptions = useMemo(
    () => (templates?.length ? templates : DISCHARGE_SUMMARY_TEMPLATES),
    [templates]
  );

  const handleFollowUpInstructionSelect = useCallback(
    (selected) => {
      const text = selected?.description || selected?.label || "";
      if (text) {
        setValue("followUpInstructions", rtfToPlainText(text));
      }
    },
    [setValue]
  );

  const handleConditionOnDischargeSelect = useCallback(
    (selected) => {
      const text = selected?.description || selected?.label || "";
      if (text) {
        setValue("conditionOnDischarge", rtfToPlainText(text));
      }
    },
    [setValue]
  );

  const applySummaryToForm = useCallback(
    (summary) => {
      if (!summary) {
        dispatch(setDischargeSummaryId(null));
        return;
      }
      dispatch(setDischargeSummaryId(summary.dischargeSummaryId));
      const patch = mapSummaryToFormValues(summary, MOCK_DOCTOR_OPTIONS);
      Object.entries(patch).forEach(([field, value]) => {
        setValue(field, value);
      });
    },
    [dispatch, setValue]
  );

  const handleSelectPatient = async (row) => {
    if (!row) {
      handleResetForm();
      return;
    }
    const mapped = mapAdmissionRowToPatient(row);
    dispatch(setPatient(mapped));
    setValue("patientSearch", {
      id: row.admissionId || row.uhid,
      label: row.patientName || "",
      value: row.contact || "",
    });

    if (!mapped.admissionId) return;
    const result = await dispatch(
      fetchDischargeSummaryByAdmission(mapped.admissionId)
    );
    if (fetchDischargeSummaryByAdmission.fulfilled.match(result)) {
      if (result.payload) {
        applySummaryToForm(result.payload);
      } else {
        dispatch(setDischargeSummaryId(null));
        const defaults = getDefaultFormValues();
        Object.entries(defaults).forEach(([field, value]) => {
          if (field === "patientSearch") return;
          setValue(field, value);
        });
        setTreatmentGivenRows([createEmptyTreatmentRow()]);
        setTreatmentAdvisedRows([createEmptyTreatmentRow()]);
        setInvestigationRows([createEmptyInvestigationRow(0)]);
      }
    } else {
      dispatch(setDischargeSummaryId(null));
      showErrorToast(result.payload || "Failed to load discharge summary");
    }
  };

  const handlePatientSearchSelect = (selectedOption) => {
    if (!selectedOption) {
      handleSelectPatient(null);
      return;
    }
    if (selectedOption.row) {
      handleSelectPatient(selectedOption.row);
    }
  };

  const handleTreatmentCellChange = (setter) => (rowId, key, value) => {
    setter((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [key]: value } : row))
    );
  };

  const handleInvestigationCellChange = (rowId, key, value) => {
    setInvestigationRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [key]: value } : row))
    );
  };

  const handleTemplateSelect = (selectedTemplate) => {
    if (!selectedTemplate?.data) {
      return;
    }

    const {
      treatmentGivenRows: templateTreatmentGiven,
      treatmentAdvisedRows: templateTreatmentAdvised,
      investigationRows: templateInvestigation,
      ...formFields
    } = selectedTemplate.data;

    Object.entries(formFields).forEach(([field, value]) => {
      if (
        field === "followUpInstructions" ||
        field === "conditionOnDischarge" ||
        field === "doctorNotes"
      ) {
        setValue(field, rtfToPlainText(value || ""));
        return;
      }
      setValue(field, value);
    });

    if (templateTreatmentGiven?.length) {
      setTreatmentGivenRows(
        templateTreatmentGiven.map((row) => ({
          ...createEmptyTreatmentRow(),
          ...row,
          id: row.id || createEmptyTreatmentRow().id,
        }))
      );
    }
    if (templateTreatmentAdvised?.length) {
      setTreatmentAdvisedRows(
        templateTreatmentAdvised.map((row) => ({
          ...createEmptyTreatmentRow(),
          ...row,
          id: row.id || createEmptyTreatmentRow().id,
        }))
      );
    }
    if (templateInvestigation?.length) {
      setInvestigationRows(
        templateInvestigation.map((row, index) => ({
          ...createEmptyInvestigationRow(index),
          ...row,
          id: row.id || createEmptyInvestigationRow(index).id,
        }))
      );
    }

    showInfoToast("Template applied to all tabs");
  };

  const handleResetForm = () => {
    reset(getDefaultFormValues());
    setTreatmentGivenRows([createEmptyTreatmentRow()]);
    setTreatmentAdvisedRows([createEmptyTreatmentRow()]);
    setInvestigationRows([createEmptyInvestigationRow(0)]);
    dispatch(resetDischargeSummary());
    setActiveTab("finalDiagnosis");
  };

  const handleSave = handleSubmit(async (data) => {
    if (!patient?.patientName) {
      showWarningToast("Please select a patient first");
      return;
    }

    const formData = {
      ...data,
      treatmentGivenRows,
      treatmentAdvisedRows,
      investigationRows,
      activeTab,
    };

    const result = await dispatch(
      saveDischargeSummaryToApi({
        formData,
        patient,
        userId: authUser?.userId || 1,
        dischargeSummaryId,
      })
    );

    if (saveDischargeSummaryToApi.fulfilled.match(result)) {
      const id =
        result.payload?.dischargeSummaryId ||
        result.payload?.result?.dischargeSummaryId;
      if (id) dispatch(setDischargeSummaryId(id));
      showSuccessToast(
        dischargeSummaryId
          ? "Discharge summary updated successfully"
          : "Discharge summary saved successfully"
      );
      return;
    }

    showErrorToast(result.payload || "Failed to save discharge summary");
  });

  const patientName = patient?.patientName || "—";
  const uhid = patient?.uhid || "—";
  const genderAge =
    patient?.patientGender && patient?.patientAge
      ? `${patient.patientGender} | ${patient.patientAge}`
      : "—";
  const doctorName = patient?.doctorName || "—";
  const currentBed =
    [patient?.wardName, patient?.bedNo].filter(Boolean).join(" - ") || "—";

  const patientInfoHeader = patient ? (
    <div className="bg-gray-100 rounded-lg p-3 border border-gray-200 shadow-sm">
      <div className="flex items-center gap-6">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
            <span className="text-lg font-semibold text-gray-600">
              {patientName !== "—" ? patientName.charAt(0).toUpperCase() : "?"}
            </span>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-3 text-sm">
          <div className="flex items-baseline gap-2 col-span-1">
            <span className="text-[#217E90] font-semibold">{patientName}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">UHID</span>
            <span className="text-[#217E90] font-semibold">: {uhid}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Gender / Age</span>
            <span className="text-[#217E90] font-semibold">: {genderAge}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Doctor name</span>
            <span className="text-[#217E90] font-semibold">: {doctorName}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Current bed</span>
            <span className="text-[#217E90] font-semibold">: {currentBed}</span>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
      Search and select a patient to begin the discharge summary entry.
    </div>
  );

  const tabContent = useMemo(() => {
    if (activeTab === "finalDiagnosis") {
      return (
        <InputAreaField
          name="finalDiagnosis"
          control={control}
          label="Final Diagnosis"
          rows={6}
          fontSize="text-xs"
          inputBgColor="bg-white"
          enableSpeechToText
        />
      );
    }

    if (activeTab === "clinicalFinding") {
      return (
        <div className="space-y-3">
          <InputAreaField
            name="clinicalFinding"
            control={control}
            label="Clinical Finding"
            rows={12}
            fontSize="text-xs"
            inputBgColor="bg-white"
            isFloatingLabel
            enableSpeechToText
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 border-t border-gray-200">
            <InputField name="bp" control={control} label="BP" placeholder="__/__" fontSize="text-xs" isFloatingLabel />
            <InputField name="pulse" control={control} label="Pulse" type="number" fontSize="text-xs" isFloatingLabel />
            <InputField name="resp" control={control} label="Resp." type="number" fontSize="text-xs" isFloatingLabel />
            <InputField name="spo2" control={control} label="SpO2" type="number" fontSize="text-xs" isFloatingLabel />
          </div>
        </div>
      );
    }

    if (activeTab === "history") {
      return (
        <div className="space-y-3">
          <InputAreaField
            name="history"
            control={control}
            label="History"
            rows={12}
            fontSize="text-xs"
            inputBgColor="bg-white"
            isFloatingLabel
            enableSpeechToText
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-gray-200">
            <InputField name="allergy" control={control} label="Allergy" fontSize="text-xs" isFloatingLabel />
            <InputField name="reaction" control={control} label="Reaction" fontSize="text-xs" isFloatingLabel />
          </div>
        </div>
      );
    }

    if (activeTab === "investigation") {
      return (
        <div className="space-y-3">
          <InputAreaField
            name="investigation"
            control={control}
            label="Investigation"
            rows={6}
            fontSize="text-xs"
            inputBgColor="bg-white"
          />
          <InputAreaField
            name="otherInvestigation"
            control={control}
            label="Other Investigation"
            rows={3}
            fontSize="text-xs"
            inputBgColor="bg-white"
          />
          <div>
            <p className="text-xs font-semibold text-[#217E90] mb-2">Investigation In Details</p>
            <EditableDataTable
              columns={INVESTIGATION_TABLE_COLUMNS}
              rows={investigationRows}
              onCellChange={handleInvestigationCellChange}
              highlightFirstCell
            />
          </div>
        </div>
      );
    }

    if (activeTab === "operativeNote") {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-3">
              <CommonDatePicker
                name="operativeDate"
                control={control}
                label="Ope Date"
                type="date"
                fontSize="text-xs"
                isFloatingLabel
              />
            </div>
            <div className="sm:col-span-3">
              <CommonTimePicker
                name="operativeTime"
                control={control}
                label="Ope Time"
                fontSize="text-xs"
                isFloatingLabel
                use24Hour={false}
                format="hh:mm A"
              />
            </div>
            <div className="sm:col-span-6 flex items-center sm:pb-2">
              <CommonCheckbox
                name="fetchLatestOperativeNotes"
                control={control}
                label="Fetch Latest Operative Notes"
                fontSize="text-xs"
                checkboxSize="h-3.5 w-3.5"
              />
            </div>
          </div>
          <InputAreaField
            name="operativeNotes"
            control={control}
            label="Operative Notes"
            rows={5}
            fontSize="text-xs"
            inputBgColor="bg-white"
            isFloatingLabel
            enableSpeechToText
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InputAreaField
              name="implantDetails"
              control={control}
              label="Implant Details"
              rows={2}
              fontSize="text-xs"
              inputBgColor="bg-white"
              isFloatingLabel
            />
            <InputAreaField
              name="instrumentDetails"
              control={control}
              label="Instrument Details"
              rows={2}
              fontSize="text-xs"
              inputBgColor="bg-white"
              isFloatingLabel
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InputAreaField
              name="procedureIndication"
              control={control}
              label="Procedure indication"
              rows={2}
              fontSize="text-xs"
              inputBgColor="bg-white"
              isFloatingLabel
            />
            <InputAreaField
              name="operativeProc"
              control={control}
              label="Operative Proc"
              rows={2}
              fontSize="text-xs"
              inputBgColor="bg-white"
              isFloatingLabel
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
            <div className="lg:col-span-3">
              <SearchDropdown
                name="surgeonName"
                control={control}
                label="Surgeon Name"
                dataArray={MOCK_DOCTOR_OPTIONS}
                isSearchable
                isClearable
                fontSize="text-xs"
                isFloatingLabel
              />
            </div>
            <div className="lg:col-span-3">
              <SearchDropdown
                name="asstSurgeonName"
                control={control}
                label="Asst. Surgeon name"
                dataArray={MOCK_DOCTOR_OPTIONS}
                isSearchable
                isClearable
                fontSize="text-xs"
                isFloatingLabel
              />
            </div>
            <div className="lg:col-span-2">
              <SearchDropdown
                name="anaestheticsName"
                control={control}
                label="Anaesthetics Name"
                dataArray={MOCK_DOCTOR_OPTIONS}
                isSearchable
                isClearable
                fontSize="text-xs"
                isFloatingLabel
              />
            </div>
            <div className="lg:col-span-2">
              <SearchDropdown
                name="anaesthesiaType"
                control={control}
                label="Anaesthesia Type"
                dataArray={ANAESTHESIA_TYPE_OPTIONS}
                isSearchable
                isClearable
                fontSize="text-xs"
                isFloatingLabel
              />
            </div>
            <div className="lg:col-span-2">
              <CommonButton
                label="OT List"
                type="button"
                size="sm"
                className="bg-red-600 text-white hover:bg-red-700 text-xs h-9 w-full"
                onClick={() => showInfoToast("OT List — coming soon")}
              />
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === "courseInHospital") {
      return (
        <div className="space-y-4">
          <TreatmentTableSection
            title="Treatment Given"
            checkboxName="useReadymadePreGiven"
            control={control}
            rows={treatmentGivenRows}
            onCellChange={handleTreatmentCellChange(setTreatmentGivenRows)}
            namePrefix="tg"
            drugOptions={drugs}
            doseOptions={doses}
            onDrugSearch={handleDrugSearch}
            onDoseSearch={handleDoseSearch}
            drugsLoading={drugsLoading}
            dosesLoading={dosesLoading}
          />
          <TreatmentTableSection
            title="Treatment Advised After Discharge"
            checkboxName="useReadymadePreAdvised"
            control={control}
            rows={treatmentAdvisedRows}
            onCellChange={handleTreatmentCellChange(setTreatmentAdvisedRows)}
            namePrefix="ta"
            drugOptions={drugs}
            doseOptions={doses}
            onDrugSearch={handleDrugSearch}
            onDoseSearch={handleDoseSearch}
            drugsLoading={drugsLoading}
            dosesLoading={dosesLoading}
          />
        </div>
      );
    }

    if (activeTab === "treatmentDetails") {
      return (
        <div className="space-y-3">
          <InputAreaField
            name="treatmentGivenText"
            control={control}
            label="Treatment Given"
            rows={5}
            fontSize="text-xs"
            inputBgColor="bg-white"
            enableSpeechToText
          />
          <InputAreaField
            name="treatmentAdvisedText"
            control={control}
            label="Treatment Advised After Discharge"
            rows={5}
            fontSize="text-xs"
            inputBgColor="bg-white"
            enableSpeechToText
          />
        </div>
      );
    }

    if (activeTab === "followUp") {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-3">
              <CommonDatePicker
                name="followUpDate"
                control={control}
                label="Follow UP Date"
                type="date"
                fontSize="text-xs"
                isFloatingLabel
              />
            </div>
            <div className="sm:col-span-3">
              <CommonTimePicker
                name="followUpTime"
                control={control}
                label="Follow UP Time"
                fontSize="text-xs"
                isFloatingLabel
                use24Hour={false}
                format="hh:mm A"
              />
            </div>
            <div className="sm:col-span-6 flex items-center sm:pb-2">
              <CommonCheckbox
                name="bookAppointment"
                control={control}
                label="Book Appointment"
                fontSize="text-xs"
                checkboxSize="h-3.5 w-3.5"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-3">
              <SearchDropdown
                name="followUpInstructionSelect"
                control={control}
                label="Follow Up Instructions"
                dataArray={followUpInstructions}
                onChange={handleFollowUpInstructionSelect}
                isLoading={followUpInstructionsLoading}
                isSearchable
                isClearable
                fontSize="text-xs"
                isFloatingLabel
              />
              <InputAreaField
                name="followUpInstructions"
                control={control}
                label="Follow Up Instructions Details"
                rows={3}
                fontSize="text-xs"
                inputBgColor="bg-white"
                isFloatingLabel
              />
            </div>
            <div className="space-y-3">
              <SearchDropdown
                name="conditionOnDischargeSelect"
                control={control}
                label="Condition On Discharge"
                dataArray={CONDITION_ON_DISCHARGE_OPTIONS}
                onChange={handleConditionOnDischargeSelect}
                isSearchable
                isClearable
                fontSize="text-xs"
                isFloatingLabel
              />
              <InputAreaField
                name="conditionOnDischarge"
                control={control}
                label="Condition On Discharge Details"
                rows={3}
                fontSize="text-xs"
                inputBgColor="bg-white"
                isFloatingLabel
              />
            </div>
          </div>

          <InputAreaField
            name="doctorNotes"
            control={control}
            label="Doctor Notes"
            rows={3}
            fontSize="text-xs"
            inputBgColor="bg-white"
            isFloatingLabel
            enableSpeechToText
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SearchDropdown name="doctor1" control={control} label="Doctor" dataArray={MOCK_DOCTOR_OPTIONS} isSearchable isClearable fontSize="text-xs" isFloatingLabel />
            <SearchDropdown name="doctor2" control={control} label="Doctor" dataArray={MOCK_DOCTOR_OPTIONS} isSearchable isClearable fontSize="text-xs" isFloatingLabel />
            <SearchDropdown name="doctor3" control={control} label="Doctor" dataArray={MOCK_DOCTOR_OPTIONS} isSearchable isClearable fontSize="text-xs" isFloatingLabel />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SearchDropdown name="dischargeType" control={control} label="Discharge Type" dataArray={DISCHARGE_TYPE_OPTIONS} isSearchable isClearable fontSize="text-xs" isFloatingLabel />
            <InputField name="doctorAssistant" control={control} label="Doctor Assi." fontSize="text-xs" isFloatingLabel />
            <InputField name="weight" control={control} label="Weight" type="number" fontSize="text-xs" isFloatingLabel />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <InputField name="emergencyContact" control={control} label="Emergency Cont" fontSize="text-xs" isFloatingLabel />
            <SearchDropdown name="whenToContact" control={control} label="When To Cont" dataArray={CONTACT_WHEN_OPTIONS} isSearchable isClearable fontSize="text-xs" isFloatingLabel />
            <SearchDropdown name="howToContact" control={control} label="How To Cont" dataArray={CONTACT_HOW_OPTIONS} isSearchable isClearable fontSize="text-xs" isFloatingLabel />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InputField name="dietAdvice" control={control} label="Diet Advice" fontSize="text-xs" isFloatingLabel />
            <InputField name="physiotherapy" control={control} label="Physiotherapy" fontSize="text-xs" isFloatingLabel />
          </div>
        </div>
      );
    }

    return null;
  }, [
    activeTab,
    control,
    investigationRows,
    treatmentGivenRows,
    treatmentAdvisedRows,
    handleConditionOnDischargeSelect,
    handleFollowUpInstructionSelect,
    followUpInstructions,
    followUpInstructionsLoading,
    drugs,
    doses,
    drugsLoading,
    dosesLoading,
    handleDrugSearch,
    handleDoseSearch,
  ]);

  return (
    <div className="p-2 sm:p-3 pb-3">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="sticky top-0 z-10 rounded-t-lg px-3 py-2 border-b border-gray-200 bg-white shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold text-[#217E90] flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-0.5 rounded hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#217E90]"
              aria-label="Go back"
            >
              <ArrowUturnLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            </button>
            <span>Discharge Summary</span>
          </h2>
        </div>

        <div className="p-3 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
            <div className="w-full sm:w-[40%]">
              <SearchDropdown
                name="patientSearch"
                control={control}
                dataArray={patientSearchOptions}
                onChange={handlePatientSearchSelect}
                label="Search by patient name or mobile number"
                isSearchable
                isClearable
                fontSize="text-xs sm:text-sm"
                isFloatingLabel
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3 sm:ml-auto w-full sm:w-auto">
              <div className="w-full sm:w-56">
                <SearchDropdown
                  name="template"
                  control={control}
                  label="Template"
                  dataArray={templateOptions}
                  onChange={handleTemplateSelect}
                  placeholder="Select"
                  isSearchable
                  isClearable
                  fontSize="text-xs sm:text-sm"
                  isFloatingLabel
                />
              </div>
              <div className="flex-shrink-0">
                <CommonButton
                  label={patientsLoading ? "Loading..." : "Admitted Patients"}
                  type="button"
                  size="sm"
                  className="bg-[#217E90] text-white hover:bg-[#1a5e70] px-3 py-1.5 text-xs sm:text-sm w-full sm:w-auto"
                  onClick={() => setAdmittedModalOpen(true)}
                  disabled={patientsLoading}
                />
              </div>
            </div>
          </div>

          {patientInfoHeader}

          {patient && (
            <div className="w-full rounded border border-gray-200 bg-white">
              <div className="flex items-center border-b border-gray-200 overflow-x-auto">
                <div className="flex flex-nowrap">
                  {DISCHARGE_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors border-b-2 -mb-px ${
                        activeTab === tab.id
                          ? "text-[#217E90] border-[#217E90] bg-white"
                          : "text-gray-500 border-transparent hover:text-gray-700"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-3 space-y-3">
                {tabContent}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <PrintButton
                    type="button"
                    onClick={() => window.print()}
                    size="sm"
                    className="h-8 !px-5 text-xs"
                  />
                  <CommonButton
                    label="Reset"
                    type="button"
                    variant="danger-outline"
                    onClick={handleResetForm}
                    className="h-8 !px-5 text-xs"
                  />
                  <CommonButton
                    label={saving ? "Saving..." : "Save"}
                    type="button"
                    variant="primary-filled"
                    onClick={handleSave}
                    disabled={saving}
                    className="h-8 !px-5 text-xs bg-[#217E90] hover:bg-[#1a6575]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AdmittedPatientsModal
        isOpen={admittedModalOpen}
        onClose={() => setAdmittedModalOpen(false)}
        onSelectPatient={handleSelectPatient}
        onRowDoubleClick={handleSelectPatient}
        rows={admissionRows}
      />
    </div>
  );
};

export default DischargeSummary;
